import { Type, Check, Bold, AlignLeft, AlignCenter, AlignRight } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTextStyle } from "@/context/TextStyleContext";
import { useI18n } from "@/context/I18nContext";

const FONT_COLORS = [
  { value: "", name: "Default" },
  { value: "#2C3325", name: "Espresso" },
  { value: "#6B8E4F", name: "Olive" },
  { value: "#1D4ED8", name: "Blue" },
  { value: "#B45309", name: "Amber" },
  { value: "#9333EA", name: "Purple" },
  { value: "#DC2626", name: "Red" },
  { value: "#000000", name: "Black" },
];

const CELL_BGS = [
  { value: "", name: "Default" },
  { value: "#FFFFFF", name: "White" },
  { value: "#FFF8E1", name: "Cream" },
  { value: "#E8F5E9", name: "Mint" },
  { value: "#FFE0E6", name: "Pink" },
  { value: "#E3F2FD", name: "Sky" },
  { value: "#FFF3E0", name: "Peach" },
  { value: "#EDE7F6", name: "Lavender" },
];

const SIZES = [
  { key: "sm", labelKey: "text.size_sm" },
  { key: "md", labelKey: "text.size_md" },
  { key: "lg", labelKey: "text.size_lg" },
];

const ALIGNS = [
  { key: "left", labelKey: "text.align_left", Icon: AlignLeft },
  { key: "center", labelKey: "text.align_center", Icon: AlignCenter },
  { key: "right", labelKey: "text.align_right", Icon: AlignRight },
];

export default function TextStyleMenu() {
  const { style, update, reset } = useTextStyle();
  const { t } = useI18n();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="flex items-center gap-1.5 bg-cream-2 hover:bg-app-border px-2.5 py-1.5 rounded-lg transition-colors text-xs font-semibold"
          data-testid="text-style-toggle"
          title={t("text.menu")}
        >
          <Type className="w-3.5 h-3.5 text-espresso/70" />
          {style.bold && <Bold className="w-3 h-3 text-olive" />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 p-2">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-olive">
          {t("text.menu")}
        </DropdownMenuLabel>

        {/* Bold toggle */}
        <button
          type="button"
          onClick={() => update({ bold: !style.bold })}
          className="w-full flex items-center justify-between gap-2 px-2 py-2 rounded-md hover:bg-cream-2 text-sm"
          data-testid="text-bold-toggle"
        >
          <span className="flex items-center gap-2">
            <Bold className="w-3.5 h-3.5" />
            <span className="font-semibold">{t("text.bold")}</span>
          </span>
          <span
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
              style.bold ? "bg-olive" : "bg-app-border"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                style.bold ? "translate-x-4" : "translate-x-0.5"
              }`}
            />
          </span>
        </button>

        <DropdownMenuSeparator />

        {/* Font size */}
        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-olive">
          {t("text.size")}
        </DropdownMenuLabel>
        <div className="flex gap-1 px-1 pb-2" data-testid="text-size-row">
          {SIZES.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => update({ size: s.key })}
              className={`flex-1 px-2 py-1.5 text-xs rounded-md border transition-colors ${
                style.size === s.key
                  ? "bg-olive text-white border-olive"
                  : "bg-white border-app-border hover:bg-cream-2"
              }`}
              data-testid={`text-size-${s.key}`}
            >
              {t(s.labelKey)}
            </button>
          ))}
        </div>

        <DropdownMenuSeparator />

        {/* Font color */}
        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-olive">
          {t("text.font_color")}
        </DropdownMenuLabel>
        <div className="grid grid-cols-8 gap-1 px-1 pb-2" data-testid="text-font-color-row">
          {FONT_COLORS.map((c) => (
            <button
              key={c.value || "default"}
              type="button"
              onClick={() => update({ fontColor: c.value })}
              title={c.name}
              className={`relative w-6 h-6 rounded-full border-2 transition-all ${
                style.fontColor === c.value
                  ? "border-olive scale-110"
                  : "border-app-border"
              }`}
              style={{ backgroundColor: c.value || "#FFFFFF" }}
              data-testid={`text-font-color-${c.value || "default"}`}
            >
              {!c.value && (
                <span className="absolute inset-0 grid place-items-center text-[8px] font-bold text-espresso/40">
                  A
                </span>
              )}
              {style.fontColor === c.value && c.value && (
                <Check className="w-3 h-3 absolute inset-0 m-auto text-white drop-shadow" />
              )}
            </button>
          ))}
        </div>

        <DropdownMenuSeparator />

        {/* Cell background */}
        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-olive">
          {t("text.cell_bg")}
        </DropdownMenuLabel>
        <div className="grid grid-cols-8 gap-1 px-1 pb-2" data-testid="text-cell-bg-row">
          {CELL_BGS.map((c) => (
            <button
              key={c.value || "default"}
              type="button"
              onClick={() => update({ cellBg: c.value })}
              title={c.name}
              className={`relative w-6 h-6 rounded-md border-2 transition-all ${
                style.cellBg === c.value
                  ? "border-olive scale-110"
                  : "border-app-border"
              }`}
              style={{ backgroundColor: c.value || "transparent" }}
              data-testid={`text-cell-bg-${c.value || "default"}`}
            >
              {!c.value && (
                <span className="absolute inset-0 grid place-items-center text-[8px] font-bold text-espresso/40">
                  —
                </span>
              )}
              {style.cellBg === c.value && c.value && (
                <Check className="w-3 h-3 absolute inset-0 m-auto text-espresso drop-shadow" />
              )}
            </button>
          ))}
        </div>

        <DropdownMenuSeparator />

        {/* Text alignment */}
        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-olive">
          {t("text.align")}
        </DropdownMenuLabel>
        <div className="flex gap-1 px-1 pb-2" data-testid="text-align-row">
          {ALIGNS.map(({ key, labelKey, Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => update({ align: key })}
              className={`flex-1 px-2 py-1.5 text-xs rounded-md border transition-colors flex items-center justify-center gap-1 ${
                style.align === key
                  ? "bg-olive text-white border-olive"
                  : "bg-white border-app-border hover:bg-cream-2"
              }`}
              data-testid={`text-align-${key}`}
              title={t(labelKey)}
            >
              <Icon className="w-3 h-3" />
              <span className="hidden sm:inline">{t(labelKey)}</span>
            </button>
          ))}
        </div>

        <DropdownMenuSeparator />

        <button
          type="button"
          onClick={reset}
          className="w-full px-2 py-1.5 text-xs text-espresso/70 hover:text-espresso hover:bg-cream-2 rounded-md"
          data-testid="text-style-reset"
        >
          {t("text.reset")}
        </button>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
